import React from 'react';

import OverridableCell from './OverridableCell';

import { formatDate } from 'Helpers/dateUtils';
import { formatFigure } from 'Helpers/formatService';

/**
 * MasterplanTableRow
 *
 * Extra props for override support:
 *   venueId        {number}
 *   sportId        {number|null}
 *   overrides      {object}  — the overrides map from the API: "{venueId}:{sportId}:{col}" => value
 *   rawValues      {object}  — original (source) values keyed by column name
 *   onSave         {fn(venueId, sportId, col, val)}
 *   onRevert       {fn(venueId, sportId, col)}
 *   isTotalRow     {bool}    — if true, cells are read-only (no override UI)
 *   isGrandTotal   {bool}    — if true, render as the inverted charcoal grand total row
 */
function MasterplanTableRow(props) {
    const { venueId, sportId, overrides = {}, rawValues = {}, onSave, onRevert, isTotalRow = false, isGrandTotal = false } = props;

    const overrideKey = (col) => `${venueId}:${sportId ?? ''}:${col}`;
    const isOverridden = (col) => !isTotalRow && Object.prototype.hasOwnProperty.call(overrides, overrideKey(col));
    const originalValue = (col) => (rawValues[col] !== undefined ? rawValues[col] : props.row[numericColIndex(col)]);

    // Map column name to row index for positional access
    const NUMERIC_COLS = [
        'athletes', // index 3
        'team_support', // index 4
        'total_team', // index 5
        'td', // index 6
        'ito', // index 7
        'nto', // index 8
        'total_officials', // index 9
        'preliminary_session', // index 10
        'medal_sessions', // index 11
        'total_sessions', // index 12
        'session_days', // index 13
        'event_days', // index 14
    ];

    const numericColIndex = (col) => NUMERIC_COLS.indexOf(col) + 3;

    // Cell background must be applied per-cell on the grand-total row — `inbud-numeric-cell`
    // sets its own background at higher specificity than a row-level `<tr>` class can override.
    const cellBgClass = isGrandTotal ? ' !tw-bg-charcoal' : '';
    const numericClass = isGrandTotal
        ? `inbud-numeric-cell tw-font-semibold tw-text-white${cellBgClass}`
        : 'inbud-numeric-cell tw-font-semibold tw-text-charcoal';

    const renderOverridableCell = (col) => {
        const idx = numericColIndex(col);
        const currentVal = props.row[idx];
        const origVal = originalValue(col);

        if (isTotalRow) {
            return (
                <td key={col} className={numericClass}>
                    {formatFigure(currentVal, (v) => String(Number(v)), { precision: 2 })}
                </td>
            );
        }

        return (
            <OverridableCell
                key={col}
                value={currentVal}
                originalValue={origVal}
                isOverridden={isOverridden(col)}
                onSave={(val) => onSave && onSave(venueId, sportId, col, val)}
                onRevert={() => onRevert && onRevert(venueId, sportId, col)}
            />
        );
    };

    // Grand total row: inverted charcoal background, white text, rounded bottom corners
    // Subtotal row: quiet grey band with top/bottom border
    // Regular row: default white background
    const grandTotalRowStyle = isGrandTotal ? { borderBottomLeftRadius: '8px' } : {};
    const stickyClass = isGrandTotal ? '!tw-bg-charcoal' : isTotalRow ? '!tw-bg-charcoal-10' : '!tw-bg-white';
    const subtotalRowStyle = isTotalRow && !isGrandTotal ? { borderTop: '1px solid #C6C6C7', borderBottom: '1px solid #C6C6C7' } : {};

    const firstCellExtraProps = isTotalRow ? { scope: 'row' } : {};
    const firstCellStyle = isGrandTotal
        ? { fontWeight: 600, ...grandTotalRowStyle }
        : isTotalRow
          ? { fontWeight: 500, color: '#545556' }
          : { fontWeight: 'normal' };

    return (
        <tr className={isGrandTotal ? 'tw-bg-charcoal' : isTotalRow ? 'tw-bg-charcoal-10' : ''} style={subtotalRowStyle}>
            <td {...firstCellExtraProps} className={`${stickyClass} tw-sticky tw-left-0 tw-z-10 tw-h-fit`} style={firstCellStyle}>
                {isGrandTotal ? (
                    <span className="tw-text-white">{props.row[0]}</span>
                ) : isTotalRow ? (
                    props.row[0]
                ) : props.row[0] !== '-' ? (
                    <a href={props.venueurl}>{props.row[0]}</a>
                ) : (
                    props.row[0]
                )}
            </td>
            <td className={`${stickyClass} tw-font-semibold tw-sticky tw-left-56 tw-z-10 tw-h-fit${isGrandTotal ? ' tw-text-white' : ''}`}>
                {isTotalRow ? props.row[1] : props.row[1] !== '-' ? <a href={props.sportUrl}>{props.row[1]}</a> : props.row[1]}
            </td>
            <td className={`${stickyClass} tw-sticky tw-left-[28rem] tw-z-10 tw-h-fit${isGrandTotal ? ' tw-text-white' : ''}`}>{props.row[2]}</td>
            {!!props.includes['athletes'] && renderOverridableCell('athletes')}
            {!!props.includes['team_support'] && renderOverridableCell('team_support')}
            {!!props.includes['total_team'] && renderOverridableCell('total_team')}
            {!!props.includes['td'] && renderOverridableCell('td')}
            {!!props.includes['ito'] && renderOverridableCell('ito')}
            {!!props.includes['nto'] && renderOverridableCell('nto')}
            {!!props.includes['total_officials'] && renderOverridableCell('total_officials')}
            {!!props.includes['preliminary_session'] && renderOverridableCell('preliminary_session')}
            {!!props.includes['medal_sessions'] && renderOverridableCell('medal_sessions')}
            {!!props.includes['total_sessions'] && renderOverridableCell('total_sessions')}
            {!!props.includes['session_days'] && renderOverridableCell('session_days')}
            {!!props.includes['event_days'] && renderOverridableCell('event_days')}
            {!!props.includes['venue_type'] && <td className={`${isGrandTotal ? 'tw-text-white' : ''}${cellBgClass}`}>{isTotalRow ? '' : props.row[15]}</td>}
            {!!props.includes['venue_capacity'] && (
                <td className={`inbud-numeric-cell${isGrandTotal ? ' tw-text-white' : ''}${cellBgClass}`}>
                    {isTotalRow ? '' : formatFigure(props.row[16], (v) => String(Number(v)))}
                </td>
            )}
            {!!props.includes['hire_start'] && (
                <td className={`inbud-numeric-cell${isGrandTotal ? ' tw-text-white' : ''}${cellBgClass}`}>{isTotalRow ? '' : formatDate(props.row[17])}</td>
            )}
            {!!props.includes['hire_end'] && (
                <td className={`inbud-numeric-cell${isGrandTotal ? ' tw-text-white' : ''}${cellBgClass}`}>{isTotalRow ? '' : formatDate(props.row[18])}</td>
            )}
            {!!props.includes['revenues'] && (
                <td className={`!tw-border-r-2 inbud-numeric-cell${cellBgClass}`}>
                    <span className={`tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>
                        {isTotalRow ? props.row[19] : formatFigure(props.row[19])}
                    </span>
                </td>
            )}
            {!!props.includes['expenses'] &&
                !!props.rowSpans &&
                (props.isSplit ? (
                    <td rowSpan={isTotalRow ? 1 : props.rowSpans} className={`tw-w-[350px]${cellBgClass}`}>
                        <div className="tw-flex tw-justify-between tw-mt-2">
                            <span className={`tw-w-1/3 tw-text-center tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>
                                {isTotalRow ? '' : formatFigure(props.row[20])}
                            </span>
                            <span className={`tw-w-1/3 tw-text-center tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>
                                {isTotalRow ? '' : formatFigure(props.row[21])}
                            </span>
                            <span className={`tw-w-1/3 tw-text-center tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>
                                {isTotalRow ? '' : formatFigure(props.row[22])}
                            </span>
                        </div>
                    </td>
                ) : (
                    <td
                        rowSpan={isTotalRow ? 1 : props.rowSpans}
                        className={`inbud-numeric-cell${cellBgClass}`}
                        style={{ borderBottomRightRadius: isGrandTotal && !props.includes['net'] ? '8px' : undefined }}
                    >
                        <span className={`tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>{isTotalRow ? '' : formatFigure(props.row[22])}</span>
                    </td>
                ))}
            {!!props.includes['net'] && !!props.rowSpans && (
                <td
                    rowSpan={isTotalRow ? 1 : props.rowSpans}
                    className={`inbud-numeric-cell${cellBgClass}`}
                    style={{ borderBottomRightRadius: isGrandTotal ? '8px' : undefined }}
                >
                    <span className={`tw-font-semibold${isGrandTotal ? ' tw-text-white' : ''}`}>{isTotalRow ? '' : formatFigure(props.row[23])}</span>
                </td>
            )}
        </tr>
    );
}

export default MasterplanTableRow;
